'use client';
import {
  privateQueryContext,
  privateQueryOptions,
} from '@eliadmin/core/src/utils/private-query-context';
import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { fetchOrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useForm } from 'react-hook-form';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { FiCheckCircle, FiInfo, FiXCircle } from 'react-icons/fi';
import { MdErrorOutline } from 'react-icons/md';
import { ProfileOrderStatus } from '@eliadmin/core/src/components/profile/profile-order-status';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import Link from '@eliadmin/core/src/components/link';
import { useSearchParams } from 'next/navigation';

export type OrderStatusFormProps = {
  className?: {
    root?: string;
    field?: string;
  };
};

type OrderStatusFormValues = {
  order_identifier: string;
  email: string;
};

export function OrderStatusForm({ className }: OrderStatusFormProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const me = useCustomerStore(s => s.me);
  const searchParams = useSearchParams();

  // Get search params
  const orderIdParam = searchParams.get('identifier') || '';
  const emailParam = searchParams.get('email') || '';

  const form = useForm<OrderStatusFormValues>({
    defaultValues: {
      email: emailParam,
      order_identifier: orderIdParam,
    },
  });

  const [searchQuery, setSearchQuery] = useState({
    email: emailParam,
    identifier: orderIdParam,
  });
  const debouncedQuery = useDebounce(searchQuery, 500);

  // Update form and search query when search params change
  useEffect(() => {
    if (orderIdParam || emailParam) {
      form.setValue('email', emailParam);
      form.setValue('order_identifier', orderIdParam);
      setSearchQuery({ email: emailParam, identifier: orderIdParam });
    }
  }, [orderIdParam, emailParam, form]);

  const { data: orderData, isLoading } = useQuery({
    queryKey: [CacheKey.ORDER_INFO, ...privateQueryContext(locale, debouncedQuery)],
    ...privateQueryOptions,
    queryFn: () =>
      fetchOrderInfo({
        locale,
        identifier: debouncedQuery.identifier,
        email: debouncedQuery.email,
      }).catch(() => null),
    enabled: debouncedQuery.email.length > 1 && debouncedQuery.identifier.length > 1,
  });

  return (
    <Form {...form}>
      <form className={cn('flex w-full flex-col gap-8 p-0 md:gap-10', className?.root)}>
        {/* If user is logged in, show info box and button to ORDERS */}
        {me && (
          <div className="mb-4 flex flex-col gap-4 rounded-none border border-border bg-white px-4 py-3 text-foreground md:flex-row md:items-center md:justify-between md:gap-4">
            <span className="text-left">{t('text3201')}</span>
            <Button className="w-full md:h-10 md:w-auto" type="button">
              <Link href={ROUTES.ORDERS() ?? '#'} passHref legacyBehavior>
                {t('text154')}
              </Link>
            </Button>
          </div>
        )}
        {/* Infobox or loader logic */}
        {isLoading ? (
          <GenericLoader className="w-full justify-center" />
        ) : !searchQuery.identifier || !searchQuery.email ? (
          <div className="mt-2 flex items-center gap-2 rounded-none border border-info-foreground bg-info px-4 py-3 text-info-foreground">
            <FiInfo className="h-5 w-5 text-info-foreground" />
            <span>{t('text2346')}</span>
          </div>
        ) : !orderData?.data && searchQuery.identifier && searchQuery.email ? (
          <div className="mt-2 flex items-center gap-2 rounded-none border border-destructive-foreground bg-destructive px-4 py-3 text-destructive-foreground">
            <MdErrorOutline className="h-5 w-5 text-destructive-foreground" />
            <span>{t('text2347')}</span>
          </div>
        ) : null}

        {/* Responsive input fields row */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          <FormField
            name="order_identifier"
            control={form.control}
            render={({ field }) => (
              <FormItem className={className?.field}>
                <FormLabel>{t('text108')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={t('text108')}
                    onChange={e => {
                      setSearchQuery(prev => ({ ...prev, identifier: e.target.value }));
                      field.onChange(e);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <FormItem className={className?.field}>
                <FormLabel>{t('text35')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={t('text259')}
                    onChange={e => {
                      setSearchQuery(prev => ({ ...prev, email: e.target.value }));
                      field.onChange(e);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {orderData?.data &&
        searchQuery.email === debouncedQuery.email &&
        searchQuery.identifier === debouncedQuery.identifier ? (
          <div className="flex flex-col gap-6 rounded-none border p-4 shadow-sm">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-xl font-bold">{t('')}</h3>
              {/* Use ProfileOrderStatus for status badge */}
              {orderData.data.status && <ProfileOrderStatus order={orderData.data} />}
            </div>

            <Separator />

            <OrderLoyaltyReward reward={orderData.data.loyalty_reward} />
            {/* Customer info */}
            <div className="grid grid-cols-1 gap-4">
              <InfoRow label={t('text1250')}>
                {orderData.data.name} {orderData.data.surname}
              </InfoRow>
              <InfoRow label={t('text35')}>{orderData.data.email}</InfoRow>
              <InfoRow label={t('text157')}>
                {orderData.data.address} {orderData.data.street_number}
              </InfoRow>
              <InfoRow label={t('')}>
                {orderData.data.postcode} {orderData.data.city}
              </InfoRow>
              <InfoRow label={t('text519')}>{orderData.data.country?.name}</InfoRow>
            </div>

            {orderData.data.different_delivery_address && (
              <>
                <Separator />
                <h4 className="text-lg font-semibold">{t('text773')}</h4>
                <div className="grid grid-cols-1 gap-4">
                  <InfoRow label={t('text157')}>
                    {orderData.data.address2} {orderData.data.street_number2}
                  </InfoRow>
                  <InfoRow label={t('')}>
                    {orderData.data.postcode2} {orderData.data.city2}
                  </InfoRow>
                  <InfoRow label={t('text519')}>{orderData.data.country2?.name}</InfoRow>
                </div>
              </>
            )}

            <Separator />

            {/* Shipment & Payment */}
            <div className="grid grid-cols-1 gap-4">
              <InfoRow label={t('text153')}>
                <div className="flex items-center gap-2">
                  {orderData.data.shipment?.image?.url && (
                    <Image
                      src={orderData.data.shipment.image.url}
                      alt={resolveImageAlt(
                        orderData?.data?.shipment?.image,
                        orderData?.data?.shipment?.name,
                      )}
                      width={36}
                      height={36}
                      className="h-9 w-9 object-contain"
                    />
                  )}
                  <span>{orderData.data.shipment?.name}</span>
                </div>
              </InfoRow>

              <InfoRow label={t('text124')}>
                <div className="flex items-center gap-2">
                  {orderData.data.payment_type?.image?.url && (
                    <Image
                      src={orderData.data.payment_type.image.url}
                      alt={resolveImageAlt(
                        orderData?.data?.payment_type?.image,
                        orderData?.data?.payment_type?.name,
                      )}
                      width={36}
                      height={36}
                      className="h-9 w-9 object-contain"
                    />
                  )}
                  <span>{orderData.data.payment_type?.name}</span>
                </div>
              </InfoRow>

              <InfoRow label={t('text164')} className="text-lg font-bold">
                {formatPrice(orderData.data.price, currency, currencySettings)}
              </InfoRow>

              <InfoRow label={t('')}>{orderData.data.paid ? t('') : t('')}</InfoRow>
            </div>

            <Separator />

            <InfoRow label={t('text18')}>
              {new Date(orderData.data.created_at).toLocaleString(locale)}
            </InfoRow>
            <Separator />
            <div className="flex flex-col gap-2">
              <h2 className="font-medium text-muted-foreground">{t('text158')}</h2>

              <div className="flex items-center gap-2">
                {orderData.data.paid ? (
                  <div className="flex w-full flex-col items-center justify-center gap-2 rounded-none bg-success p-6">
                    <FiCheckCircle className="h-8 w-8 text-success-foreground" />
                    <span className="text-lg font-bold text-success-foreground sm:text-2xl md:text-4xl">
                      {t('text2311')}
                    </span>
                  </div>
                ) : (
                  <div className="flex w-full flex-col items-center justify-center gap-2">
                    {orderData.data.paid ? (
                      <div className="flex w-full flex-col items-center justify-center gap-2 rounded-none bg-success p-6">
                        <FiCheckCircle className="h-8 w-8 text-success-foreground" />
                        <span className="text-lg font-bold text-success-foreground sm:text-2xl md:text-4xl">
                          {t('text2311')} {/* e.g., Zaplatená */}
                        </span>
                      </div>
                    ) : (
                      <div className="flex w-full flex-col items-center justify-center gap-4 rounded-none bg-destructive p-6">
                        <FiXCircle className="h-8 w-8 text-white" />
                        <span className="text-lg font-bold text-white sm:text-2xl md:text-4xl">
                          {t('text2312')} {/* e.g., Nezaplatená */}
                        </span>
                      </div>
                    )}
                    {!orderData.data.paid && orderData.data.payment_url && (
                      <Button
                        asChild
                        variant="default"
                        className="w-full gap-2 bg-success text-lg text-success-foreground hover:bg-success/70 hover:text-success-foreground/70 sm:text-xl md:text-2xl"
                      >
                        <a
                          href={orderData.data.payment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {orderData.data.payment_type?.image?.url && (
                            <Image
                              src={orderData.data.payment_type.image.url}
                              alt={resolveImageAlt(
                                orderData?.data?.payment_type?.image,
                                orderData?.data?.payment_type?.name,
                              )}
                              width={36}
                              height={36}
                              className="h-8 w-8 object-contain sm:h-10 sm:w-10 md:h-12 md:w-12"
                            />
                          )}
                          {t('text207')}
                        </a>
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Documents */}
            {orderData.data.documents?.length > 0 && (
              <>
                <Separator />
                <h4 className="text-lg font-semibold">{t('text804')}</h4>
                <ul className="list-disc pl-4">
                  <p>{t('text3129')}</p>
                  <Button asChild className="mt-4 w-fit">
                    <Link prefetch={false} href={ROUTES.ORDERS()}>
                      {t('text257')}
                    </Link>
                  </Button>
                </ul>
              </>
            )}

            {/* Items */}
            {orderData.data.items?.length > 0 && (
              <>
                <Separator />
                <h4 className="text-lg font-semibold">{t('')}</h4>
                <div className="flex flex-col gap-4">
                  {orderData.data.items.map(item => (
                    <div
                      key={item.id}
                      className="flex h-28 flex-row items-center justify-between gap-2 rounded-none border p-2"
                    >
                      {item?.image?.url && (
                        <Image
                          src={item.image?.url ?? '/images/placeholder.png'}
                          alt={resolveImageAlt(item?.image, item?.name ?? 'image')}
                          width={0}
                          height={0}
                          sizes="100vw"
                          className="h-auto max-h-[100px] w-auto max-w-[100px] object-cover"
                        />
                      )}

                      <div className="flex flex-grow flex-col gap-2">
                        <span className="font-bold">{item.name}</span>
                        <span className="font-bold">
                          {formatPrice(
                            roundPrice(item.price_with_vat || item.price),
                            currency,
                            currencySettings,
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          !isLoading && <div className="text-center text-muted-foreground">{t('')}</div>
        )}
      </form>
    </Form>
  );
}

function InfoRow({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between text-sm', className)}>
      <span className="font-medium text-muted-foreground">{label}</span>
      <span>{children}</span>
    </div>
  );
}
